import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FirearmSpecificClient, FirearmSpecificDTO } from '../services/apiClient';
import { API_BASE_URL, MAX_DROPDOWN_PAGE_SIZE } from '../config/config';
import axiosInstance from '../services/axiosInstance';

const getFirearmSpecificClient = () => new FirearmSpecificClient(API_BASE_URL, axiosInstance);

// TanStack Query Hooks

// Cached lookup for all firearm specifics (for admin pages)
export const useFirearmSpecifics = () =>
  useQuery({
    queryKey: ['firearmSpecifics'],
    queryFn: async () => {
      const response = await getFirearmSpecificClient().searchFirearmSpecifics({
        filters: { isActive: true },
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Cached lookup for dropdowns (filtered by type)
export const useFirearmSpecificsByType = (firearmTypeId: string | undefined) =>
  useQuery({
    queryKey: ['firearmSpecifics', firearmTypeId],
    queryFn: async () => {
      const response = await getFirearmSpecificClient().searchFirearmSpecifics({
        filters: { firearmTypeId },
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      return response?.data?.results || [];
    },
    staleTime: 4 * 60 * 60 * 1000, // 4 hours
    refetchOnReconnect: true,
  });

// Non-cached search for admin tables
export const useFirearmSpecificSearch = () => {
  const search = async (params: any) => {
    const response = await getFirearmSpecificClient().searchFirearmSpecifics(params);
    return {
      results: response?.data?.results || [],
      totalCount: response?.data?.totalCount || 0,
    };
  };
  return { search };
};

// Get by ID
export const useFirearmSpecificById = (id: string) =>
  useQuery({
    queryKey: ['firearmSpecific', id],
    queryFn: async () => {
      const response = await getFirearmSpecificClient().getFirearmSpecificById(id);
      return response?.data;
    },
    enabled: !!id,
  });

// Create mutation
export const useCreateFirearmSpecific = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: FirearmSpecificDTO) => {
      await getFirearmSpecificClient().createFirearmSpecific(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmSpecifics'] });
    },
  });
};

// Update mutation
export const useUpdateFirearmSpecific = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: FirearmSpecificDTO }) => {
      await getFirearmSpecificClient().updateFirearmSpecific(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmSpecifics'] });
    },
  });
};

// Delete mutation
export const useDeleteFirearmSpecific = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await getFirearmSpecificClient().deleteFirearmSpecific(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['firearmSpecifics'] });
    },
  });
};
// Get by ID with exercises (for score forms)
export const useFirearmSpecificWithExercisesById = (id: string | undefined) =>
  useQuery({
    queryKey: ['firearmSpecificWithExercises', id],
    queryFn: async () => {
      const response = await getFirearmSpecificClient().getFirearmSpecificByIdWithExercises(id!);
      return response?.data;
    },
    enabled: !!id,
  });
export const useFirearmSpecificWithPurposes = (id: string | undefined) =>
  useQuery({
    queryKey: ['firearmSpecific', id],
    queryFn: async () => {
      const response = await getFirearmSpecificClient().getFirearmSpecificById(id!);
      return response?.data || null;
    },
    enabled: !!id,
    staleTime: Infinity,
    refetchOnReconnect: true,
  });